import { PythonTopicExercise } from '../../../../types/python-topic';

export const contenedoresErroresExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Tuplas: Datos inmutables',
    description: 'Aprende a crear y usar tuplas, colecciones ordenadas e inmutables en Python.',
    initialCode: `# Crea una tupla con información de un producto
# (nombre, precio, stock)
producto = 

# Intenta acceder a los elementos
nombre = producto[0]
precio = producto[1]
stock = producto[2]

print(f"Producto: {nombre}")
print(f"Precio: $" + str(precio))
print(f"Stock: {stock}")`,
    solution: `# Crea una tupla con información de un producto
# (nombre, precio, stock)
producto = ("Laptop", 999.99, 5)

# Intenta acceder a los elementos
nombre = producto[0]
precio = producto[1]
stock = producto[2]

print(f"Producto: {nombre}")
print(f"Precio: $" + str(precio))
print(f"Stock: {stock}")`,
    testCases: [],
    hints: [
      'Las tuplas usan paréntesis ()',
      'Los elementos se separan por comas',
      'Las tuplas son inmutables (no se pueden modificar)',
      'Puedes acceder a elementos con índices [0], [1], etc.'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar tuplas?',
      content: `Las **tuplas** son colecciones ordenadas e inmutables. Son útiles cuando necesitas agrupar datos relacionados que no deben cambiar.

### ¿Por qué este ejercicio?
- **Inmutabilidad**: Los datos no pueden modificarse accidentalmente
- **Agrupación**: Permite agrupar datos relacionados
- **Aplicación práctica**: Coordenadas, configuraciones, datos constantes
- **Eficiencia**: Más eficientes en memoria que listas para datos fijos

### En el mundo real:
Cuando un sistema almacena coordenadas GPS (latitud, longitud), usa tuplas porque estas coordenadas no deben cambiar. Sin tuplas, tendrías que usar listas que podrían modificarse accidentalmente.`,
      keyPoints: [
        'Las tuplas son inmutables (no se pueden modificar)',
        'Usan paréntesis () en lugar de corchetes []',
        'Ideal para datos que no deben cambiar',
        'Puedes acceder a elementos con índices'
      ],
      realWorldExample: 'En un sistema de geolocalización, las coordenadas (latitud, longitud) se almacenan como tuplas porque no deben modificarse.'
    },
    theory: `# Tuplas en Python

## ¿Qué es una Tupla?

Una **tupla** es una colección ordenada e **inmutable** (no se puede modificar) de elementos.

## Sintaxis

Las tuplas se crean con paréntesis:

\`\`\`python
producto = ("Laptop", 999.99, 5)
coordenadas = (40.7128, -74.0060)
\`\`\`

## Características

### 1. Inmutabilidad
Una vez creada, no puedes modificar una tupla:

\`\`\`python
producto = ("Laptop", 999.99, 5)
producto[0] = "Mouse"  # Error! No se puede modificar
\`\`\`

### 2. Acceso por Índice
Puedes acceder a elementos con índices:

\`\`\`python
producto = ("Laptop", 999.99, 5)
nombre = producto[0]   # "Laptop"
precio = producto[1]   # 999.99
stock = producto[2]    # 5
\`\`\`

### 3. Desempaquetado
Puedes asignar elementos a variables:

\`\`\`python
producto = ("Laptop", 999.99, 5)
nombre, precio, stock = producto
\`\`\`

## Cuándo Usar Tuplas

- **Datos que no cambian**: Coordenadas, configuraciones
- **Agrupación de datos**: Información relacionada que va junta
- **Claves en diccionarios**: Las tuplas pueden ser claves (las listas no)

## Ventajas

- **Inmutabilidad**: Previene modificaciones accidentales
- **Eficiencia**: Más eficientes en memoria que listas
- **Claridad**: Indica que los datos no deben cambiar`,
    tips: [
      'Las tuplas usan paréntesis ()',
      'Los elementos se separan por comas',
      'Puedes acceder a elementos con índices [0], [1], etc.',
      'Las tuplas son inmutables - no puedes modificarlas después de crearlas',
      'Puedes desempaquetar tuplas: nombre, precio = producto'
    ],
    advice: [
      'Usa tuplas cuando los datos no deben cambiar',
      'Las tuplas son más eficientes que listas para datos fijos',
      'Puedes usar tuplas como claves en diccionarios',
      'Considera tuplas para agrupar datos relacionados'
    ]
  },
  {
    id: 2,
    title: 'Sets: Colecciones sin duplicados',
    description: 'Aprende a crear y usar sets, colecciones que no permiten elementos duplicados.',
    initialCode: `# Crea un set con números
numeros = 

# Agrega un número duplicado
numeros.add(5)

# Verifica si un número está en el set
tiene_tres = 3 in numeros

print(f"Set: {numeros}")
print(f"¿Tiene 3?: {tiene_tres}")`,
    solution: `# Crea un set con números
numeros = {1, 2, 3, 4, 5}

# Agrega un número duplicado
numeros.add(5)

# Verifica si un número está en el set
tiene_tres = 3 in numeros

print(f"Set: {numeros}")
print(f"¿Tiene 3?: {tiene_tres}")`,
    testCases: [],
    hints: [
      'Los sets usan llaves {}',
      'No permiten elementos duplicados',
      'Puedes agregar elementos con add()',
      'Puedes verificar pertenencia con "in"'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar sets?',
      content: `Los **sets** son colecciones que no permiten elementos duplicados. Son útiles cuando necesitas valores únicos o verificar pertenencia rápidamente.

### ¿Por qué este ejercicio?
- **Eliminar duplicados**: Automáticamente mantiene valores únicos
- **Verificación rápida**: Comprobar si un elemento existe es muy rápido
- **Aplicación práctica**: IDs únicos, tags, categorías
- **Operaciones de conjunto**: Unión, intersección, diferencia

### En el mundo real:
Cuando un sistema de usuarios necesita verificar si un email ya está registrado, usa un set para almacenar emails. Sin sets, tendrías que recorrer toda la lista cada vez que verificas.`,
      keyPoints: [
        'Los sets no permiten elementos duplicados',
        'Usan llaves {} como diccionarios, pero sin valores',
        'Ideal para eliminar duplicados y verificar pertenencia',
        'Operaciones de conjunto (unión, intersección) son muy útiles'
      ],
      realWorldExample: 'En un sistema de tags, un set almacena todos los tags únicos. Cuando agregas un tag duplicado, simplemente se ignora.'
    },
    theory: `# Sets en Python

## ¿Qué es un Set?

Un **set** es una colección **desordenada** de elementos **únicos** (sin duplicados).

## Sintaxis

Los sets se crean con llaves:

\`\`\`python
numeros = {1, 2, 3, 4, 5}
tags = {"python", "programacion", "datos"}
\`\`\`

## Características

### 1. Sin Duplicados
Los sets automáticamente eliminan duplicados:

\`\`\`python
numeros = {1, 2, 2, 3, 3, 3}
print(numeros)  # {1, 2, 3}
\`\`\`

### 2. Verificación Rápida
Verificar si un elemento existe es muy rápido:

\`\`\`python
numeros = {1, 2, 3, 4, 5}
tiene_tres = 3 in numeros  # True
tiene_diez = 10 in numeros  # False
\`\`\`

### 3. Agregar Elementos
Puedes agregar elementos con \`add()\`:

\`\`\`python
numeros = {1, 2, 3}
numeros.add(4)
numeros.add(2)  # No se agrega porque ya existe
\`\`\`

## Operaciones de Conjunto

### Unión
\`\`\`python
a = {1, 2, 3}
b = {3, 4, 5}
union = a | b  # {1, 2, 3, 4, 5}
\`\`\`

### Intersección
\`\`\`python
a = {1, 2, 3}
b = {3, 4, 5}
interseccion = a & b  # {3}
\`\`\`

### Diferencia
\`\`\`python
a = {1, 2, 3}
b = {3, 4, 5}
diferencia = a - b  # {1, 2}
\`\`\`

## Cuándo Usar Sets

- **Eliminar duplicados**: De listas o colecciones
- **Verificar pertenencia**: Comprobar si un elemento existe
- **Operaciones de conjunto**: Unión, intersección, diferencia
- **Valores únicos**: Cuando necesitas garantizar unicidad

## Ventajas

- **Eliminación automática de duplicados**
- **Verificación rápida de pertenencia**
- **Operaciones de conjunto eficientes**
- **Garantiza valores únicos**`,
    tips: [
      'Los sets usan llaves {}',
      'No permiten elementos duplicados',
      'Puedes agregar elementos con add()',
      'Puedes verificar pertenencia con "in"',
      'Los sets son desordenados (no tienen índice)'
    ],
    advice: [
      'Usa sets cuando necesites valores únicos',
      'Los sets son ideales para eliminar duplicados de listas',
      'Verificar pertenencia en sets es muy rápido',
      'Considera sets para operaciones de conjunto (unión, intersección)'
    ]
  },
  {
    id: 3,
    title: 'Diccionarios: Clave-Valor',
    description: 'Aprende a crear y usar diccionarios, estructuras que asocian claves con valores.',
    initialCode: `# Crea un diccionario con información de un usuario
usuario = {
    # Completa con: nombre, edad, email
}

# Accede a los valores
nombre = usuario["nombre"]
edad = usuario["edad"]

print(f"Nombre: {nombre}")
print(f"Edad: {edad}")`,
    solution: `# Crea un diccionario con información de un usuario
usuario = {
    "nombre": "María",
    "edad": 30,
    "email": "maria@example.com"
}

# Accede a los valores
nombre = usuario["nombre"]
edad = usuario["edad"]

print(f"Nombre: {nombre}")
print(f"Edad: {edad}")`,
    testCases: [],
    hints: [
      'Los diccionarios usan llaves {}',
      'Cada elemento tiene una clave y un valor',
      'Formato: "clave": valor',
      'Puedes acceder a valores con diccionario["clave"]'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar diccionarios?',
      content: `Los **diccionarios** asocian claves con valores, permitiendo acceso rápido a datos por nombre en lugar de por posición.

### ¿Por qué este ejercicio?
- **Acceso por nombre**: Más legible que acceder por índice
- **Estructura de datos**: Ideal para datos estructurados (usuarios, productos, configuraciones)
- **Aplicación práctica**: JSON, APIs, configuraciones
- **Flexibilidad**: Fácil agregar, modificar y eliminar elementos

### En el mundo real:
Cuando un sistema almacena información de usuarios, usa diccionarios: {"nombre": "Juan", "edad": 25, "email": "juan@example.com"}. Sin diccionarios, tendrías que usar listas y recordar qué índice corresponde a qué dato.`,
      keyPoints: [
        'Los diccionarios asocian claves con valores',
        'Usan llaves {} con formato "clave": valor',
        'Acceso rápido por nombre de clave',
        'Ideal para datos estructurados'
      ],
      realWorldExample: 'En un sistema de e-commerce, cada producto es un diccionario: {"nombre": "Laptop", "precio": 999.99, "stock": 5}.'
    },
    theory: `# Diccionarios en Python

## ¿Qué es un Diccionario?

Un **diccionario** es una colección de pares **clave-valor**. Cada elemento tiene una clave única que se usa para acceder a su valor.

## Sintaxis

Los diccionarios se crean con llaves:

\`\`\`python
usuario = {
    "nombre": "María",
    "edad": 30,
    "email": "maria@example.com"
}
\`\`\`

## Acceso a Valores

Puedes acceder a valores usando la clave:

\`\`\`python
usuario = {"nombre": "María", "edad": 30}
nombre = usuario["nombre"]  # "María"
edad = usuario["edad"]       # 30
\`\`\`

## Agregar y Modificar

Puedes agregar o modificar elementos:

\`\`\`python
usuario = {"nombre": "María"}
usuario["edad"] = 30        # Agregar
usuario["nombre"] = "Juan"  # Modificar
\`\`\`

## Métodos Útiles

### get()
Obtiene un valor o un valor por defecto:

\`\`\`python
usuario = {"nombre": "María"}
edad = usuario.get("edad", 0)  # 0 si no existe
\`\`\`

### keys() y values()
Obtiene todas las claves o valores:

\`\`\`python
usuario = {"nombre": "María", "edad": 30}
claves = usuario.keys()    # dict_keys(['nombre', 'edad'])
valores = usuario.values()  # dict_values(['María', 30])
\`\`\`

## Cuándo Usar Diccionarios

- **Datos estructurados**: Usuarios, productos, configuraciones
- **Acceso por nombre**: Cuando el nombre es más significativo que el índice
- **JSON/APIs**: Formato natural para datos JSON
- **Configuraciones**: Parámetros y opciones

## Ventajas

- **Acceso rápido**: Por nombre de clave
- **Legibilidad**: Código más claro que listas con índices
- **Flexibilidad**: Fácil agregar, modificar y eliminar
- **Estructura**: Ideal para datos complejos`,
    tips: [
      'Los diccionarios usan llaves {}',
      'Formato: "clave": valor',
      'Puedes acceder con diccionario["clave"]',
      'Usa get() para valores opcionales con valor por defecto',
      'Las claves deben ser inmutables (strings, números, tuplas)'
    ],
    advice: [
      'Usa diccionarios para datos estructurados',
      'Las claves deben ser descriptivas',
      'Considera usar get() en lugar de [] para evitar errores',
      'Los diccionarios son ideales para trabajar con JSON y APIs'
    ]
  },
  {
    id: 4,
    title: 'Manejo de errores con try-except',
    description: 'Aprende a manejar errores y excepciones usando try-except en Python.',
    initialCode: `# Intenta dividir dos números
def dividir(a, b):
    # Usa try-except para manejar el error de división por cero
    try:
        resultado = 
    except:
        resultado = "Error: No se puede dividir por cero"
    return resultado

# Prueba la función
print(dividir(10, 2))
print(dividir(10, 0))`,
    solution: `# Intenta dividir dos números
def dividir(a, b):
    # Usa try-except para manejar el error de división por cero
    try:
        resultado = a / b
    except:
        resultado = "Error: No se puede dividir por cero"
    return resultado

# Prueba la función
print(dividir(10, 2))
print(dividir(10, 0))`,
    testCases: [],
    hints: [
      'Usa try: para código que puede fallar',
      'Usa except: para manejar el error',
      'El código en try se ejecuta normalmente',
      'Si hay error, se ejecuta el código en except'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué manejar errores?',
      content: `El **manejo de errores** permite que tu programa continúe ejecutándose incluso cuando algo sale mal, en lugar de detenerse abruptamente.

### ¿Por qué este ejercicio?
- **Robustez**: Tu programa no se detiene por errores inesperados
- **Experiencia de usuario**: Mensajes de error claros en lugar de crashes
- **Aplicación práctica**: Validación de entrada, operaciones que pueden fallar
- **Depuración**: Identificar y manejar problemas específicos

### En el mundo real:
Cuando un sistema procesa pagos, usa try-except para manejar errores de conexión, tarjetas rechazadas, o fondos insuficientes. Sin manejo de errores, cualquier problema detendría todo el sistema.`,
      keyPoints: [
        'try-except permite manejar errores sin detener el programa',
        'El código en try se ejecuta normalmente',
        'Si hay error, se ejecuta el código en except',
        'Puedes especificar tipos de errores específicos'
      ],
      realWorldExample: 'En un sistema de lectura de archivos, try-except maneja el caso cuando el archivo no existe, mostrando un mensaje claro en lugar de un error técnico.'
    },
    theory: `# Manejo de Errores con try-except

## ¿Qué es try-except?

\`try-except\` es una estructura que permite ejecutar código y manejar errores si ocurren, sin detener el programa.

## Sintaxis Básica

\`\`\`python
try:
    # Código que puede fallar
    resultado = 10 / 0
except:
    # Código que se ejecuta si hay error
    resultado = "Error: División por cero"
\`\`\`

## Cómo Funciona

1. **try**: El código dentro de \`try\` se ejecuta normalmente
2. **Si hay error**: Se detiene la ejecución en \`try\` y se ejecuta \`except\`
3. **Si no hay error**: Se ejecuta \`try\` completamente y se omite \`except\`

## Tipos de Errores Específicos

Puedes manejar errores específicos:

\`\`\`python
try:
    numero = int("abc")
except ValueError:
    print("Error: No se puede convertir a número")
except ZeroDivisionError:
    print("Error: División por cero")
\`\`\`

## Ejemplo Completo

\`\`\`python
def dividir(a, b):
    try:
        resultado = a / b
        return resultado
    except ZeroDivisionError:
        return "Error: No se puede dividir por cero"
    except TypeError:
        return "Error: Tipos incorrectos"
\`\`\`

## Ventajas

- **Robustez**: El programa no se detiene por errores
- **Control**: Puedes manejar errores de forma específica
- **Experiencia de usuario**: Mensajes de error claros
- **Depuración**: Identificar problemas fácilmente`,
    tips: [
      'Usa try para código que puede fallar',
      'Usa except para manejar el error',
      'Puedes especificar tipos de errores específicos',
      'Siempre maneja errores esperados',
      'Usa mensajes de error claros y descriptivos'
    ],
    advice: [
      'Maneja errores que puedes anticipar',
      'Usa mensajes de error claros para el usuario',
      'Especifica tipos de errores cuando sea posible',
      'No uses except sin especificar el tipo a menos que sea necesario'
    ]
  },
  {
    id: 5,
    title: 'Desempaquetado de tuplas',
    description: 'Aprende a desempaquetar tuplas asignando sus elementos a variables.',
    initialCode: `# Crea una tupla con coordenadas
coordenadas = (10, 20)

# Desempaqueta la tupla en dos variables
# Completa: x, y = 
x = 
y = 

print(f"X: {x}, Y: {y}")`,
    solution: `# Crea una tupla con coordenadas
coordenadas = (10, 20)

# Desempaqueta la tupla en dos variables
# Completa: x, y = 
x, y = coordenadas

print(f"X: {x}, Y: {y}")`,
    testCases: [],
    hints: [
      'Puedes asignar múltiples variables de una tupla',
      'x, y = tupla desempaqueta la tupla',
      'El número de variables debe coincidir con elementos',
      'Útil para trabajar con coordenadas y pares de datos'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué desempaquetar tuplas?',
      content: `El **desempaquetado de tuplas** permite asignar múltiples variables de una sola vez, haciendo el código más legible y conciso.

### ¿Por qué es útil?
- **Legibilidad**: Código más claro que acceder por índice
- **Concisión**: Asignación múltiple en una línea
- **Aplicación práctica**: Coordenadas, pares de datos, retornos múltiples
- **Pythonic**: Estilo característico de Python

### En el mundo real:
Cuando una función devuelve múltiples valores como tupla (x, y), puedes desempaquetarlos directamente: x, y = obtener_coordenadas(). Sin desempaquetado, tendrías que acceder por índice.`,
      keyPoints: [
        'x, y = tupla desempaqueta la tupla',
        'El número de variables debe coincidir con elementos',
        'Más legible que acceder por índice',
        'Útil para funciones que devuelven múltiples valores'
      ],
      realWorldExample: 'En un sistema de gráficos, obtener_coordenadas() devuelve (x, y) y puedes desempaquetarlo directamente: x, y = obtener_coordenadas().'
    },
    theory: `# Desempaquetado de Tuplas

## ¿Qué es el Desempaquetado?

El **desempaquetado** permite asignar los elementos de una tupla a múltiples variables en una sola línea.

## Sintaxis

\`\`\`python
coordenadas = (10, 20)
x, y = coordenadas
\`\`\`

## Características

### 1. Asignación Múltiple
Puedes asignar múltiples variables:
\`\`\`python
x, y = (10, 20)
\`\`\`

### 2. Número de Variables
El número de variables debe coincidir:
\`\`\`python
x, y = (10, 20)  # Correcto
x, y, z = (10, 20)  # Error: demasiadas variables
\`\`\`

### 3. Funciones que Devuelven Tuplas
Útil con funciones que devuelven múltiples valores:
\`\`\`python
def obtener_coordenadas():
    return (10, 20)

x, y = obtener_coordenadas()
\`\`\`

## Ventajas

- **Legibilidad**: Código más claro
- **Concisión**: Asignación en una línea
- **Pythonic**: Estilo característico de Python`,
    tips: [
      'x, y = tupla desempaqueta la tupla',
      'El número de variables debe coincidir con elementos',
      'Más legible que acceder por índice',
      'Útil para funciones que devuelven múltiples valores',
      'Puedes ignorar elementos con _'
    ],
    advice: [
      'Practica desempaquetando tuplas simples',
      'Experimenta con funciones que devuelven tuplas',
      'Aprende a ignorar elementos con _',
      'Practica con tuplas de diferentes tamaños'
    ]
  },
  {
    id: 6,
    title: 'Operaciones con sets',
    description: 'Aprende a realizar operaciones de conjunto: unión, intersección y diferencia.',
    initialCode: `# Define dos sets
set1 = {1, 2, 3, 4}
set2 = {3, 4, 5, 6}

# Calcula la unión (todos los elementos de ambos)
union = 

# Calcula la intersección (elementos comunes)
interseccion = 

# Calcula la diferencia (elementos en set1 pero no en set2)
diferencia = 

print(f"Unión: {union}")
print(f"Intersección: {interseccion}")
print(f"Diferencia: {diferencia}")`,
    solution: `# Define dos sets
set1 = {1, 2, 3, 4}
set2 = {3, 4, 5, 6}

# Calcula la unión (todos los elementos de ambos)
union = set1 | set2

# Calcula la intersección (elementos comunes)
interseccion = set1 & set2

# Calcula la diferencia (elementos en set1 pero no en set2)
diferencia = set1 - set2

print(f"Unión: {union}")
print(f"Intersección: {interseccion}")
print(f"Diferencia: {diferencia}")`,
    testCases: [],
    hints: [
      '| es el operador de unión',
      '& es el operador de intersección',
      '- es el operador de diferencia',
      'Los sets eliminan duplicados automáticamente'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué operaciones con sets?',
      content: `Las **operaciones con sets** permiten combinar, comparar y analizar colecciones de datos de forma eficiente y matemáticamente precisa.

### ¿Por qué son poderosas?
- **Análisis de datos**: Encontrar elementos comunes, únicos o diferentes
- **Eficiencia**: Operaciones rápidas incluso con grandes conjuntos
- **Lógica de negocio**: Tags comunes, usuarios compartidos, categorías
- **Matemáticas**: Operaciones de teoría de conjuntos

### En el mundo real:
En un sistema de recomendaciones, la intersección de tags de dos usuarios encuentra intereses comunes. Sin operaciones de sets, tendrías que hacer bucles anidados que son lentos.`,
      keyPoints: [
        '| es unión (todos los elementos)',
        '& es intersección (elementos comunes)',
        '- es diferencia (elementos en A pero no en B)',
        'Operaciones muy eficientes'
      ],
      realWorldExample: 'En un sistema de permisos, la unión de permisos de múltiples roles encuentra todos los permisos disponibles.'
    },
    theory: `# Operaciones con Sets

## Operaciones de Conjunto

Los sets soportan operaciones matemáticas de teoría de conjuntos.

## Unión (|)

Combina todos los elementos de ambos sets:
\`\`\`python
set1 = {1, 2, 3}
set2 = {3, 4, 5}
union = set1 | set2  # {1, 2, 3, 4, 5}
\`\`\`

## Intersección (&)

Elementos que están en ambos sets:
\`\`\`python
set1 = {1, 2, 3}
set2 = {3, 4, 5}
interseccion = set1 & set2  # {3}
\`\`\`

## Diferencia (-)

Elementos en el primer set pero no en el segundo:
\`\`\`python
set1 = {1, 2, 3}
set2 = {3, 4, 5}
diferencia = set1 - set2  # {1, 2}
\`\`\`

## Ventajas

- **Eficiencia**: Operaciones muy rápidas
- **Matemáticas**: Operaciones de teoría de conjuntos
- **Análisis**: Útil para análisis de datos`,
    tips: [
      '| es unión (todos los elementos)',
      '& es intersección (elementos comunes)',
      '- es diferencia (elementos en A pero no en B)',
      'Operaciones muy eficientes',
      'Los duplicados se eliminan automáticamente'
    ],
    advice: [
      'Practica con diferentes operaciones de sets',
      'Experimenta con sets de diferentes tamaños',
      'Aprende cuándo usar cada operación',
      'Practica en contextos de análisis de datos'
    ]
  },
  {
    id: 7,
    title: 'Métodos de diccionarios',
    description: 'Aprende a usar métodos comunes de diccionarios: get(), keys(), values(), items().',
    initialCode: `# Define un diccionario
producto = {
    "nombre": "Laptop",
    "precio": 999.99,
    "stock": 5
}

# Usa get() para obtener precio con valor por defecto 0
precio = producto.get("precio", 0)

# Obtén todas las claves
claves = 

# Obtén todos los valores
valores = 

# Obtén todos los pares clave-valor
items = 

print(f"Claves: {list(claves)}")
print(f"Valores: {list(valores)}")
print(f"Items: {list(items)}")`,
    solution: `# Define un diccionario
producto = {
    "nombre": "Laptop",
    "precio": 999.99,
    "stock": 5
}

# Usa get() para obtener precio con valor por defecto 0
precio = producto.get("precio", 0)

# Obtén todas las claves
claves = producto.keys()

# Obtén todos los valores
valores = producto.values()

# Obtén todos los pares clave-valor
items = producto.items()

print(f"Claves: {list(claves)}")
print(f"Valores: {list(valores)}")
print(f"Items: {list(items)}")`,
    testCases: [],
    hints: [
      'keys() devuelve todas las claves',
      'values() devuelve todos los valores',
      'items() devuelve pares (clave, valor)',
      'get() permite valor por defecto si la clave no existe'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué métodos de diccionarios?',
      content: `Los **métodos de diccionarios** proporcionan formas eficientes de acceder, iterar y manipular datos en diccionarios, haciendo el código más robusto y legible.

### ¿Por qué son esenciales?
- **Acceso seguro**: get() evita errores de claves inexistentes
- **Iteración**: keys(), values(), items() permiten iterar eficientemente
- **Manipulación**: Métodos para agregar, eliminar y modificar
- **Aplicación práctica**: Procesamiento de datos, APIs, configuraciones

### En el mundo real:
Cuando procesas datos de una API JSON, items() permite iterar sobre todos los pares clave-valor. Sin estos métodos, tendrías que conocer todas las claves de antemano.`,
      keyPoints: [
        'get() permite valor por defecto',
        'keys() devuelve todas las claves',
        'values() devuelve todos los valores',
        'items() devuelve pares (clave, valor)'
      ],
      realWorldExample: 'En un sistema de configuración, keys() lista todas las opciones disponibles, y get() obtiene valores con defaults seguros.'
    },
    theory: `# Métodos de Diccionarios

## Métodos Comunes

Los diccionarios tienen métodos útiles para acceder y manipular datos.

## get()

Obtiene un valor o un valor por defecto:
\`\`\`python
producto = {"nombre": "Laptop"}
precio = producto.get("precio", 0)  # 0 si no existe
\`\`\`

## keys()

Obtiene todas las claves:
\`\`\`python
producto = {"nombre": "Laptop", "precio": 999.99}
claves = producto.keys()  # dict_keys(['nombre', 'precio'])
\`\`\`

## values()

Obtiene todos los valores:
\`\`\`python
producto = {"nombre": "Laptop", "precio": 999.99}
valores = producto.values()  # dict_values(['Laptop', 999.99])
\`\`\`

## items()

Obtiene todos los pares clave-valor:
\`\`\`python
producto = {"nombre": "Laptop", "precio": 999.99}
items = producto.items()  # dict_items([('nombre', 'Laptop'), ('precio', 999.99)])
\`\`\`

## Ventajas

- **Acceso seguro**: get() evita errores
- **Iteración**: Fácil iterar sobre datos
- **Manipulación**: Métodos para modificar`,
    tips: [
      'get() permite valor por defecto',
      'keys() devuelve todas las claves',
      'values() devuelve todos los valores',
      'items() devuelve pares (clave, valor)',
      'Útil para iterar sobre diccionarios'
    ],
    advice: [
      'Practica usando get() en lugar de []',
      'Experimenta iterando con items()',
      'Aprende cuándo usar cada método',
      'Practica procesando datos de diccionarios'
    ]
  },
  {
    id: 8,
    title: 'Modificar diccionarios',
    description: 'Aprende a agregar, modificar y eliminar elementos de diccionarios.',
    initialCode: `# Define un diccionario inicial
usuario = {
    "nombre": "Juan",
    "edad": 25
}

# Agrega el email
usuario["email"] = 

# Modifica la edad
usuario["edad"] = 

# Elimina el email usando del
# Completa: del usuario["email"]

print(f"Usuario: {usuario}")`,
    solution: `# Define un diccionario inicial
usuario = {
    "nombre": "Juan",
    "edad": 25
}

# Agrega el email
usuario["email"] = "juan@example.com"

# Modifica la edad
usuario["edad"] = 26

# Elimina el email usando del
# Completa: del usuario["email"]
del usuario["email"]

print(f"Usuario: {usuario}")`,
    testCases: [],
    hints: [
      'diccionario["clave"] = valor agrega o modifica',
      'del diccionario["clave"] elimina un elemento',
      'Si la clave existe, se modifica; si no, se agrega',
      'Eliminar una clave inexistente causa error'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué modificar diccionarios?',
      content: `**Modificar diccionarios** permite actualizar datos dinámicamente, agregando nueva información, cambiando valores existentes o eliminando datos innecesarios.

### ¿Por qué es esencial?
- **Actualización**: Cambiar valores según cambian los datos
- **Flexibilidad**: Agregar nuevos campos dinámicamente
- **Limpieza**: Eliminar datos innecesarios
- **Aplicación práctica**: Actualizar perfiles, configuraciones, datos de sesión

### En el mundo real:
En un sistema de usuarios, cuando un usuario actualiza su perfil, se modifican los valores del diccionario. Sin modificación de diccionarios, tendrías que recrear todo el diccionario cada vez.`,
      keyPoints: [
        'diccionario["clave"] = valor agrega o modifica',
        'del diccionario["clave"] elimina un elemento',
        'Si la clave existe, se modifica',
        'Si la clave no existe, se agrega'
      ],
      realWorldExample: 'En un sistema de carrito de compras, agregas productos con carrito["producto_id"] = cantidad y eliminas con del carrito["producto_id"].'
    },
    theory: `# Modificar Diccionarios

## Agregar y Modificar

Puedes agregar o modificar elementos:
\`\`\`python
usuario = {"nombre": "Juan"}
usuario["edad"] = 25  # Agrega
usuario["nombre"] = "María"  # Modifica
\`\`\`

## Eliminar

Puedes eliminar elementos con \`del\`:
\`\`\`python
usuario = {"nombre": "Juan", "edad": 25}
del usuario["edad"]
\`\`\`

## Método pop()

Elimina y devuelve un valor:
\`\`\`python
usuario = {"nombre": "Juan", "edad": 25}
edad = usuario.pop("edad")  # Elimina y devuelve 25
\`\`\`

## Ventajas

- **Flexibilidad**: Fácil agregar, modificar y eliminar
- **Dinámico**: Estructura adaptable
- **Eficiencia**: Operaciones rápidas`,
    tips: [
      'diccionario["clave"] = valor agrega o modifica',
      'del diccionario["clave"] elimina',
      'pop() elimina y devuelve el valor',
      'Si la clave existe, se modifica',
      'Si la clave no existe, se agrega'
    ],
    advice: [
      'Practica agregando y modificando elementos',
      'Experimenta eliminando elementos',
      'Aprende a usar pop() cuando necesites el valor',
      'Practica actualizando datos dinámicamente'
    ]
  },
  {
    id: 9,
    title: 'Errores específicos con except',
    description: 'Aprende a manejar tipos de errores específicos usando except con tipos de excepción.',
    initialCode: `# Función que puede tener diferentes errores
def procesar_numero(texto):
    try:
        numero = int(texto)
        resultado = 100 / numero
        return resultado
    except ValueError:
        # Completa: devuelve "Error: No es un número válido"
        pass
    except ZeroDivisionError:
        # Completa: devuelve "Error: No se puede dividir por cero"
        pass

# Prueba la función
print(procesar_numero("10"))
print(procesar_numero("abc"))
print(procesar_numero("0"))`,
    solution: `# Función que puede tener diferentes errores
def procesar_numero(texto):
    try:
        numero = int(texto)
        resultado = 100 / numero
        return resultado
    except ValueError:
        # Completa: devuelve "Error: No es un número válido"
        return "Error: No es un número válido"
    except ZeroDivisionError:
        # Completa: devuelve "Error: No se puede dividir por cero"
        return "Error: No se puede dividir por cero"

# Prueba la función
print(procesar_numero("10"))
print(procesar_numero("abc"))
print(procesar_numero("0"))`,
    testCases: [],
    hints: [
      'except ValueError maneja errores de conversión',
      'except ZeroDivisionError maneja división por cero',
      'Puedes tener múltiples bloques except',
      'Cada except maneja un tipo de error específico'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué manejar errores específicos?',
      content: `Manejar **errores específicos** permite tratar diferentes tipos de problemas de forma diferente, proporcionando mensajes de error más precisos y acciones apropiadas para cada situación.

### ¿Por qué es mejor?
- **Precisión**: Mensajes de error específicos y útiles
- **Control**: Diferentes acciones para diferentes errores
- **Debugging**: Identificar problemas más fácilmente
- **Robustez**: Manejar cada tipo de error apropiadamente

### En el mundo real:
En un sistema de entrada de datos, ValueError indica datos inválidos mientras que FileNotFoundError indica archivo faltante. Sin manejo específico, todos los errores se tratarían igual.`,
      keyPoints: [
        'except TipoError maneja errores específicos',
        'Puedes tener múltiples bloques except',
        'Cada except maneja un tipo diferente',
        'Mensajes de error más precisos'
      ],
      realWorldExample: 'En un sistema de archivos, FileNotFoundError muestra "Archivo no encontrado" mientras que PermissionError muestra "Sin permisos".'
    },
    theory: `# Errores Específicos con except

## Manejar Errores Específicos

Puedes manejar diferentes tipos de errores de forma específica:
\`\`\`python
try:
    numero = int("abc")
except ValueError:
    print("Error: No es un número")
except ZeroDivisionError:
    print("Error: División por cero")
\`\`\`

## Tipos Comunes de Errores

### ValueError
Error de conversión o valor inválido:
\`\`\`python
int("abc")  # ValueError
\`\`\`

### ZeroDivisionError
División por cero:
\`\`\`python
10 / 0  # ZeroDivisionError
\`\`\`

### KeyError
Clave no encontrada en diccionario:
\`\`\`python
diccionario["inexistente"]  # KeyError
\`\`\`

### IndexError
Índice fuera de rango:
\`\`\`python
lista[10]  # IndexError si lista tiene menos de 11 elementos
\`\`\`

## Ventajas

- **Precisión**: Mensajes específicos
- **Control**: Acciones diferentes por error
- **Debugging**: Identificar problemas fácilmente`,
    tips: [
      'except TipoError maneja errores específicos',
      'Puedes tener múltiples bloques except',
      'Cada except maneja un tipo diferente',
      'Mensajes de error más precisos',
      'Especifica el tipo de error cuando sea posible'
    ],
    advice: [
      'Practica manejando diferentes tipos de errores',
      'Experimenta con múltiples bloques except',
      'Aprende los tipos de errores comunes',
      'Practica proporcionando mensajes específicos'
    ]
  },
  {
    id: 10,
    title: 'finally: código siempre ejecutado',
    description: 'Aprende a usar finally para ejecutar código que siempre debe correr, haya error o no.',
    initialCode: `# Función que abre y cierra un archivo
def leer_archivo(nombre_archivo):
    archivo = None
    try:
        archivo = open(nombre_archivo, 'r')
        contenido = archivo.read()
        return contenido
    except FileNotFoundError:
        return "Error: Archivo no encontrado"
    finally:
        # Completa: cierra el archivo si está abierto
        # Usa: if archivo: archivo.close()
        pass

# Prueba la función
print(leer_archivo("test.txt"))`,
    solution: `# Función que abre y cierra un archivo
def leer_archivo(nombre_archivo):
    archivo = None
    try:
        archivo = open(nombre_archivo, 'r')
        contenido = archivo.read()
        return contenido
    except FileNotFoundError:
        return "Error: Archivo no encontrado"
    finally:
        # Completa: cierra el archivo si está abierto
        # Usa: if archivo: archivo.close()
        if archivo:
            archivo.close()

# Prueba la función
print(leer_archivo("test.txt"))`,
    testCases: [],
    hints: [
      'finally siempre se ejecuta, haya error o no',
      'Útil para limpieza (cerrar archivos, conexiones)',
      'Se ejecuta después de try y except',
      'Garantiza que el código de limpieza siempre corra'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar finally?',
      content: `**finally** garantiza que cierto código se ejecute siempre, haya error o no, esencial para limpieza de recursos como archivos, conexiones de base de datos o liberación de memoria.

### ¿Por qué es crítico?
- **Limpieza garantizada**: Código de limpieza siempre se ejecuta
- **Recursos**: Cerrar archivos, conexiones, liberar memoria
- **Consistencia**: Estado consistente sin importar errores
- **Buenas prácticas**: Previene fugas de recursos

### En el mundo real:
Cuando abres un archivo, debes cerrarlo siempre, incluso si hay un error. finally garantiza que archivo.close() siempre se ejecute. Sin finally, podrías dejar archivos abiertos.`,
      keyPoints: [
        'finally siempre se ejecuta',
        'Útil para limpieza de recursos',
        'Se ejecuta después de try y except',
        'Garantiza código de limpieza'
      ],
      realWorldExample: 'En un sistema de base de datos, finally cierra la conexión siempre, incluso si hay un error en la consulta.'
    },
    theory: `# finally: Código Siempre Ejecutado

## ¿Qué es finally?

**finally** es un bloque que siempre se ejecuta, sin importar si hay error o no.

## Sintaxis

\`\`\`python
try:
    # Código que puede fallar
    archivo = open("archivo.txt")
except FileNotFoundError:
    # Manejo de error
    print("Archivo no encontrado")
finally:
    # Siempre se ejecuta
    if archivo:
        archivo.close()
\`\`\`

## Características

### 1. Siempre se Ejecuta
Se ejecuta sin importar el resultado:
\`\`\`python
try:
    resultado = 10 / 2
finally:
    print("Siempre se ejecuta")  # Se ejecuta
\`\`\`

### 2. Después de try/except
Se ejecuta después de try y except:
\`\`\`python
try:
    # Código
except:
    # Manejo
finally:
    # Limpieza (siempre)
\`\`\`

### 3. Limpieza de Recursos
Ideal para cerrar recursos:
\`\`\`python
finally:
    archivo.close()
    conexion.cerrar()
\`\`\`

## Ventajas

- **Garantía**: Código siempre se ejecuta
- **Limpieza**: Ideal para recursos
- **Consistencia**: Estado consistente`,
    tips: [
      'finally siempre se ejecuta',
      'Útil para limpieza de recursos',
      'Se ejecuta después de try y except',
      'Garantiza código de limpieza',
      'Ideal para cerrar archivos y conexiones'
    ],
    advice: [
      'Practica usando finally para limpieza',
      'Experimenta con diferentes recursos',
      'Aprende cuándo usar finally',
      'Practica garantizando limpieza de recursos'
    ]
  }
];
